/**
 * The vendor's announcements and incidents (phase 7b item 5): current,
 * scheduled and ended, newest start first — kind, severity, audience
 * (every tenant or named ones), whether public on the status page.
 */
import type { Announcement } from '@acp/api-contract/vendor.js';
import { isInForce } from '@acp/api-contract/vendor.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { isPlainClick } from '../../lib/links.js';
import { announcementTone } from '../../lib/vendor.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AnnouncementsListProps = {
  readonly announcements: readonly Announcement[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly canAdmin: boolean;
  readonly nowMs: number;
  readonly onCreate: () => void;
  readonly onOpen: (announcementId: string) => void;
  readonly onRetry: () => void;
};

export function announcementHref(announcementId: string): string {
  return `/vendor/announcements/${encodeURIComponent(announcementId)}`;
}

/** Scheduled, in force, or ended — at `nowMs`. */
export function phaseOf(a: Pick<Announcement, 'startsAt' | 'endsAt'>, nowMs: number): 'scheduled' | 'in force' | 'ended' {
  if (isInForce(a, nowMs)) return 'in force';
  return Date.parse(a.startsAt) > nowMs ? 'scheduled' : 'ended';
}

export function AnnouncementsList({ announcements, loading, error, canAdmin, nowMs, onCreate, onOpen, onRetry }: AnnouncementsListProps) {
  return (
    <Card data-testid="announcements-list">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Announcements</CardTitle>
          <CardDescription>Shown in-app to the tenants they target while in force; public ones also on the status page.</CardDescription>
        </div>
        {canAdmin ? (
          <Button onClick={onCreate} data-testid="create-announcement">
            <PlusIcon />
            New announcement
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load announcements: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && announcements.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading announcements…</p> : null}
        {!loading && error === null && announcements.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No announcements.</p> : null}
        {announcements.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Audience</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {announcements.map((a) => {
                const phase = phaseOf(a, nowMs);
                return (
                  <TableRow key={a.announcementId} data-testid="announcement-row" data-phase={phase}>
                    <TableCell>
                      <a
                        href={announcementHref(a.announcementId)}
                        className="font-medium hover:underline"
                        data-testid="open-announcement"
                        onClick={(event) => {
                          if (!isPlainClick(event)) return;
                          event.preventDefault();
                          onOpen(a.announcementId);
                        }}
                      >
                        {a.title}
                      </a>
                    </TableCell>
                    <TableCell className="text-sm">{a.kind}</TableCell>
                    <TableCell>
                      <Badge variant={announcementTone(a.severity)}>{a.severity}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <Badge variant={phase === 'in force' ? 'onair' : 'secondary'}>{phase}</Badge>{' '}
                      <span className="text-muted-foreground">
                        {a.startsAt.slice(0, 16).replace('T', ' ')}
                        {a.endsAt === null ? ' →' : ` → ${a.endsAt.slice(0, 16).replace('T', ' ')}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.tenantIds === null ? 'every tenant' : a.tenantIds.join(', ')}
                      {a.public ? (
                        <Badge variant="standby" className="ml-2">
                          public
                        </Badge>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
